import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "../Icon";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import api from "../../services/api";
import { subscribeNotifications, getNotifications, refreshNotifications } from "../../services/notifications";

export const NotificationBell = () => {
    const navigate = useNavigate();
    // React 17 não tem useSyncExternalStore; a store expõe subscribe/getSnapshot
    // e o estado local só espelha o snapshot.
    const [{ notifications, unreadCount }, setSnapshot] = useState(getNotifications);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => subscribeNotifications(setSnapshot), []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target) &&
                (!dropdownRef.current || !dropdownRef.current.contains(e.target))
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Ancora por `bottom` quando não há espaço abaixo do sino (ex.: sino no
    // rodapé da sidebar), para o dropdown crescer para cima a partir do botão.
    const updatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const width = 320;
            const maxHeight = 420;
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
            const spaceBelow = window.innerHeight - rect.bottom;

            if (spaceBelow < maxHeight + 8 && rect.top > spaceBelow) {
                setPos({ bottom: window.innerHeight - rect.top + 8, left });
            } else {
                setPos({ top: rect.bottom + 8, left });
            }
        }
    };

    useEffect(() => {
        if (!open) return;
        window.addEventListener('resize', updatePosition);
        return () => window.removeEventListener('resize', updatePosition);
    }, [open]);

    const toggleOpen = () => {
        setOpen(prev => {
            if (!prev) updatePosition();
            return !prev;
        });
    };

    const markAllRead = () => {
        api.put('/mention-notifications/read-all').then(() => {
            refreshNotifications();
        }).catch(() => {});
    };

    const openNotification = (notification) => {
        setOpen(false);
        const goToCard = () => {
            if (notification.card_id) {
                navigate(`/crm/card/${notification.card_id}`);
            }
        };

        if (notification.read_at) {
            goToCard();
            return;
        }

        api.put(`/mention-notifications/${notification.id}/read`)
            .then(() => refreshNotifications())
            .catch(() => {})
            .then(goToCard);
    };

    return (
        <div className="notification-bell" ref={wrapperRef}>
            <button
                type="button"
                className="bell-button"
                ref={buttonRef}
                onClick={toggleOpen}
            >
                <Icon name="bell" />
                {unreadCount > 0 &&
                    <span className="badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                }
            </button>

            {open && createPortal(
                <div className="notification-dropdown" ref={dropdownRef} style={pos}>
                    <div className="dropdown-header">
                        <strong>Notificações</strong>
                        {unreadCount > 0 &&
                            <a href="#" onClick={e => { e.preventDefault(); markAllRead(); }}>
                                Marcar todas como lidas
                            </a>
                        }
                    </div>
                    <div className="dropdown-list">
                        {notifications.length === 0 &&
                            <p className="empty">Nenhuma notificação.</p>
                        }
                        {notifications.map(notification => (
                            <button
                                type="button"
                                key={notification.id}
                                className={`notification-item${notification.read_at ? '' : ' unread'}`}
                                onClick={() => openNotification(notification)}
                            >
                                <span className="title">
                                    <b>{notification.mentioned_by ?? 'Alguém'}</b> mencionou você
                                    {notification.card_code ? ` no card #${notification.card_code}` : ''}
                                </span>
                                {notification.excerpt &&
                                    <span className="excerpt">{notification.excerpt}</span>
                                }
                                <span className="time">{moment(notification.created_at).fromNow()}</span>
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default NotificationBell;
